/**
 * manager-cart-gift.js
 * Sincroniza os brindes ("compre e ganhe") do carrinho.
 * Disponível como window.CartGiftManager.
 *
 * Dependências (devem carregar antes):
 *   - window.CartDrawerManager (manager-cart-drawer.js) — emite 'cart-drawer:refreshed'
 *
 * ─── Como funciona ────────────────────────────────────────────────────────
 *
 * O snippet `cart-gift-state.liquid` (renderizado dentro do drawer) publica o
 * estado calculado em Liquid:
 *
 *   <script type="application/json" data-cart-gift-state>
 *     { "enabled": true, "expected": [...], "present": [...] }
 *   </script>
 *
 *   expected → brindes que o carrinho DEVERIA ter (gatilhos presentes)
 *   present  → linhas de brinde que o carrinho TEM hoje
 *
 * A cada refresh do drawer o manager compara os dois e aplica a diferença:
 * adiciona o brinde faltante, remove o brinde órfão (gatilho saiu, promoção
 * desativada, brinde duplicado) e corrige a quantidade.
 *
 * O preço zero NÃO é responsabilidade do tema: quem zera é um desconto
 * automático nativo do Shopify (Compre X leve Y com 100% off no brinde).
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   await window.CartGiftManager.sync()
 *   window.CartGiftManager.destroy()
 */

class CartGiftManager {
  /** Tempo máximo de espera pelo refresh do drawer antes de destravar o sync. */
  static REFRESH_TIMEOUT = 4000;

  constructor() {
    this._syncing         = false;
    this._awaitingRefresh = false;
    this._unlockTimer     = null;

    /** Variantes que falharam ao ser adicionadas (evita retentar em loop). */
    this._failed = new Set();

    this._onDrawerRefreshed = this._onDrawerRefreshed.bind(this);
    document.addEventListener('cart-drawer:refreshed', this._onDrawerRefreshed);

    this.sync();
  }

  // ─── Leitura do estado publicado pelo Liquid ─────────────────────────────

  _readState() {
    const el = document.querySelector('[data-cart-gift-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[CartGiftManager] Estado inválido:', err);
      return null;
    }
  }

  /**
   * Compara o esperado com o presente e devolve as operações necessárias.
   * @returns {{ add: object[], remove: object[], update: object[] }}
   */
  _plan(state) {
    const expected = state.enabled ? state.expected || [] : [];
    const present  = state.present || [];

    const wanted = new Map(expected.map(gift => [String(gift.variant_id), gift]));
    const kept   = new Set();
    const add = [], remove = [], update = [];

    for (const line of present) {
      const variantId = String(line.variant_id);
      const gift      = wanted.get(variantId);

      // Brinde órfão ou linha duplicada do mesmo brinde
      if (!gift || kept.has(variantId)) {
        remove.push(line);
        continue;
      }

      kept.add(variantId);
      const quantity = gift.quantity || 1;
      if (line.quantity !== quantity) update.push({ key: line.key, quantity });
    }

    for (const [variantId, gift] of wanted) {
      if (kept.has(variantId) || this._failed.has(variantId)) continue;
      add.push(gift);
    }

    return { add, remove, update };
  }

  // ─── Cart API (fetch direto para não disparar o fluxo de "item adicionado") ─

  async _post(url, payload) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || `HTTP ${res.status}`);
    }
    return res.json();
  }

  _change(key, quantity) {
    return this._post('/cart/change.js', { id: key, quantity });
  }

  _add(gifts, state) {
    const items = gifts.map(gift => ({
      id: gift.variant_id,
      quantity: gift.quantity || 1,
      properties: {
        [state.label_property]: state.label_value,
        [state.property]: gift.trigger,
      },
    }));
    return this._post('/cart/add.js', { items });
  }

  // ─── Sincronização ───────────────────────────────────────────────────────

  /** Aplica a diferença entre os brindes esperados e os presentes no carrinho. */
  async sync() {
    if (this._syncing) return { success: true, changed: false };

    const state = this._readState();
    if (!state) return { success: true, changed: false };

    const plan = this._plan(state);
    if (!plan.add.length && !plan.remove.length && !plan.update.length) {
      return { success: true, changed: false };
    }

    this._syncing = true;
    let changed = false;

    try {
      for (const line of plan.remove) {
        await this._change(line.key, 0);
        changed = true;
      }

      for (const line of plan.update) {
        await this._change(line.key, line.quantity);
        changed = true;
      }

      if (plan.add.length) {
        try {
          await this._add(plan.add, state);
          changed = true;
        } catch (err) {
          // Brinde sem estoque ou indisponível: não retenta nesta página.
          plan.add.forEach(gift => this._failed.add(String(gift.variant_id)));
          console.warn('[CartGiftManager] Não foi possível adicionar o brinde:', err.message);
        }
      }
    } catch (err) {
      console.error('[CartGiftManager] Sync falhou:', err);
    }

    if (changed) {
      // Mantém o sync travado até o drawer voltar com o estado atualizado,
      // para não recalcular o plano em cima de um DOM defasado.
      this._awaitingRefresh = true;
      this._unlockTimer = setTimeout(() => this._unlock(), CartGiftManager.REFRESH_TIMEOUT);
      document.dispatchEvent(
        new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'gift' } })
      );
    } else {
      this._syncing = false;
    }

    return { success: true, changed };
  }

  _unlock() {
    clearTimeout(this._unlockTimer);
    this._unlockTimer     = null;
    this._awaitingRefresh = false;
    this._syncing         = false;
  }

  _onDrawerRefreshed() {
    if (this._awaitingRefresh) this._unlock();
    this.sync();
  }

  destroy() {
    clearTimeout(this._unlockTimer);
    document.removeEventListener('cart-drawer:refreshed', this._onDrawerRefreshed);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.CartGiftManager = new CartGiftManager();
});
